// Study-guide reading page. Opened via Events.OpenStudy { subject }.
// Presents the subject's lore as a proper page of (fun, lightly-embellished)
// Three-Kingdoms history the player can actually read, then hands off straight
// into that subject's next quiz via a "Take the quiz" button. Lives at the body
// level so it survives the Academy panel's frequent re-renders (the old inline
// fold collapsed on every income tick because the panel rebuilds on WealthChange).

import { Events, on, emit } from './eventBus.js'
import { StudyGuides } from '../data/quizzes.js'
import { DegreeSubjectLabels, DegreeTiers } from '../config/balance.js'
import { getDegreeTier, nextTierFor } from '../game/profileManager.js'
import { formatCoins } from './format.js'

// A little emoji per subject so each page has its own identity.
const SUBJECT_ICON = Object.freeze({
  POLY_SCIENCE: '🏛',
  DIVINITY: '☯',
  AGRICULTURE: '🌾',
  EDUCATION: '📜',
  HISTORY: '⚔️',
  FINE_ARTS: '🎴',
  MILITARY: '🛡',
  FINANCE: '💰',
})

export function mountStudyModal() {
  if (document.querySelector('.study-modal')) return // idempotent

  const wrap = document.createElement('div')
  // Reuse the quiz-modal skeleton/styles (backdrop + centered card), plus a
  // study-modal modifier for the prose-specific styling.
  wrap.className = 'quiz-modal study-modal'
  wrap.innerHTML = `
    <div class="quiz-modal__backdrop" data-study-close></div>
    <div class="quiz-modal__card study-modal__card" role="dialog" aria-modal="true" aria-label="Study guide">
      <button class="quiz-modal__x" data-study-close aria-label="Close">×</button>
      <div class="study-modal__head">
        <div class="study-modal__icon" data-study-icon>📖</div>
        <div>
          <div class="study-modal__eyebrow">Academy · Study Guide</div>
          <div class="study-modal__title" data-study-title>—</div>
        </div>
      </div>
      <div class="study-modal__body" data-study-body></div>
      <div class="study-modal__foot" data-study-foot></div>
    </div>
  `
  document.body.appendChild(wrap)

  const iconEl = wrap.querySelector('[data-study-icon]')
  const titleEl = wrap.querySelector('[data-study-title]')
  const bodyEl = wrap.querySelector('[data-study-body]')
  const footEl = wrap.querySelector('[data-study-foot]')

  const close = () => wrap.classList.remove('open')
  wrap.querySelectorAll('[data-study-close]').forEach((b) => b.addEventListener('click', close))
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && wrap.classList.contains('open')) close()
  })

  const open = ({ subject }) => {
    const lore = StudyGuides[subject]
    if (!lore) return
    const label = DegreeSubjectLabels[subject] ?? subject
    iconEl.textContent = SUBJECT_ICON[subject] ?? '📖'
    titleEl.textContent = label

    // Break the single lore blob into readable paragraphs (every ~2 sentences)
    // so it reads like a page rather than a wall of text. First letter gets a
    // drop-cap via CSS. We SPLIT (lossless) rather than match — a regex match
    // silently drops sentences whose period sits inside quotes ("…nobles.").
    // The lookbehind splits only on whitespace that follows end-punctuation,
    // so a period inside a closing quote won't break the sentence.
    const sentences = lore.split(/(?<=[.!?])\s+/).filter(Boolean)
    const paras = []
    for (let i = 0; i < sentences.length; i += 2) {
      paras.push(sentences.slice(i, i + 2).join(' ').trim())
    }
    bodyEl.innerHTML = paras
      .map((p, i) => `<p class="study-modal__p${i === 0 ? ' study-modal__p--lead' : ''}">${escapeHtml(p)}</p>`)
      .join('')

    // Foot: hand off into the next quiz tier (or note the PhD is complete).
    const current = getDegreeTier(subject)
    const next = nextTierFor(subject)
    if (!next) {
      footEl.innerHTML = `
        <div class="study-modal__hint">🎓 You hold the <strong>PhD</strong> in ${escapeHtml(label)} — the highest honor. Nothing left to study here!</div>
      `
    } else {
      const tierName = DegreeTiers[next]?.name ?? next
      const cost = DegreeTiers[next]?.cost ?? 0
      footEl.innerHTML = `
        <div class="study-modal__hint">${current ? `You hold the <strong>${DegreeTiers[current]?.name ?? current}</strong>. ` : ''}Ready? The <strong>${escapeHtml(tierName)}</strong> quiz is 5 questions — answer 3 right to earn the degree. The <strong>${formatCoins(cost)}</strong> exam fee is charged per attempt, win or lose.</div>
        <button class="quiz-modal__next study-modal__quizbtn" data-take-quiz>Take the ${escapeHtml(tierName)} quiz →</button>
      `
      footEl.querySelector('[data-take-quiz]').addEventListener('click', () => {
        close()
        emit(Events.OpenQuiz, { subject, tier: next })
      })
    }

    bodyEl.scrollTop = 0
    wrap.classList.add('open')
  }

  on(Events.OpenStudy, open)
}

function escapeHtml(s) {
  return String(s).replace(/[&<>"']/g, (c) => (
    { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]
  ))
}
